import React, { useEffect, useRef } from "react";
import { CopyableText, StepNumberCircle, Table } from "@truevault/aquilo";
import clsx from "clsx";

export type DomainSetupDnsRecord = {
  recordType: string;
  name: string;
  value: string;
  id: string;
};

const copyableTextClassName =
  "bg-[#F5F4F9] px-[8px] py-[3px] rounded-[4px] hover:bg-[#F5F4F9] [&>div:first-child>span]:text-[12px] [&>div:first-child>span]:leading-[20px] [&>div:first-child>span]:text-[#1A1726] [&>div:first-child>span]:font-mono [&>div:first-child>svg]:text-[#918CA1]";
const dnsTableCopyableTextClassName = `${copyableTextClassName} w-[244px] [&>div:first-child]:w-full [&>div:first-child]:justify-between`;
const DNS_TABLE_COPYABLE_MAX_WIDTH = "210px";

const truncateForDisplay = (text: string, max = 24): string =>
  text.length > max ? `${text.slice(0, max)}…` : text;

export function DomainSetupStepSection({
  number,
  title,
  children,
  subtitle,
  childrenClassName,
  onViewed,
}: {
  number: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  childrenClassName?: string;
  onViewed?: () => void;
}) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const hasBeenViewedRef = useRef(false);

  useEffect(() => {
    if (!onViewed || !sectionRef.current || hasBeenViewedRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenViewedRef.current) {
          hasBeenViewedRef.current = true;
          onViewed();
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [onViewed]);

  return (
    <div ref={sectionRef} className="flex gap-[12px] mt-[24px] first:mt-0">
      <div className="shrink-0 self-start">
        <StepNumberCircle text={number} />
      </div>
      <div className="flex-1 text-[14px] text-neutral-900">
        <h3 className="m-0 text-[18px] font-medium leading-[28px] text-neutral-900">{title}</h3>
        {subtitle && <div className="mt-[6px]">{subtitle}</div>}
        {children && (
          <div className={clsx("mt-[12px] space-y-[12px]", childrenClassName)}>{children}</div>
        )}
      </div>
    </div>
  );
}

export function DomainSetupDocumentationLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a href={href} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export function DomainSetupInlineCopyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex w-full min-w-0 items-center gap-[12px] text-[14px] text-[#1A1726]">
      <span className="shrink-0 font-bold leading-[21px]">{label}</span>
      <div className="min-w-0 flex-1">
        <CopyableText
          shortText={value}
          longText={value}
          tooltipText="Copy"
          maxWidth="100%"
          className={copyableTextClassName}
        />
      </div>
    </div>
  );
}

export function DomainSetupDnsRecordsTable<T extends DomainSetupDnsRecord>({
  records,
  renderStatus,
}: {
  records: T[];
  renderStatus?: (record: T) => React.ReactNode;
}) {
  return (
    <Table<T>
      data={records}
      keyExtractor={(row) => row.id}
      className="domain-setup-dns-table"
      columns={[
        {
          key: "recordType",
          header: "RECORD TYPE",
          className: "w-[150px]",
          render: (row) => (
            <div className="flex items-center gap-[6px]">
              <span className="text-[13px] font-medium text-neutral-900">{row.recordType}</span>
              {renderStatus?.(row)}
            </div>
          ),
        },
        {
          key: "name",
          header: "NAME",
          className: "w-[250px]",
          render: (row) => (
            <CopyableText
              shortText={truncateForDisplay(row.name, 30)}
              longText={row.name}
              tooltipText="Copy"
              maxWidth={DNS_TABLE_COPYABLE_MAX_WIDTH}
              className={dnsTableCopyableTextClassName}
            />
          ),
        },
        {
          key: "value",
          header: "VALUE",
          className: "w-[250px]",
          render: (row) => (
            <CopyableText
              shortText={truncateForDisplay(row.value, 30)}
              longText={row.value}
              tooltipText="Copy"
              maxWidth={DNS_TABLE_COPYABLE_MAX_WIDTH}
              className={dnsTableCopyableTextClassName}
            />
          ),
        },
      ]}
    />
  );
}
